import LabelSelectList from '@/components/LabelSelectList'
import TableTabButton from '@/components/TableTabButton'
import { DATE_TIME_FORMAT } from '@/libs/core/constants'
import fieldToSortStr from '@/libs/utils/fieldToSortStr'
import Button from '@/shared/Button'
import ConfirmDialog from '@/shared/ConfirmDialog'
import FormCreator from '@/shared/FormCreator'
import Table from '@/shared/Table'
import AddCircle from '@mui/icons-material/AddCircle'
import Edit from '@mui/icons-material/Edit'
import Close from '@mui/icons-material/HighlightOff'
import Preview from '@mui/icons-material/Preview'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import get from 'lodash/get'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import usePermissions from '@/hooks/usePermissions'
import Redirect from '@/components/Redirect'
import PermissionsController from '@/components/PermissionsController'

const mapStateToProps = ({ landingPage, loading }) => ({
  landingPageList: landingPage.landingPageList,
  landingPageLabels: landingPage.landingPageLabels,
  isLoading: loading['landingPage/getLandingPages'],
  isLabelsLoading: loading['landingPage/getLandingPageLabels'],
  deleteLoading: loading['landingPage/deleteLandingPage'],
})

const fieldMap = {
  name: 'LANDINGPAGE_NAME',
  htmlTitle: 'LANDINGPAGE_TITLE',
  labelNames: 'LANDINGPAGE_LABELNAMES',
  contain: 'LANDINGPAGE_CONTAIN',
  lastUpdateTime: 'LANDINGPAGE_UPDATETIME',
}

const LandingPageTemplate = ({
  dispatch,
  landingPageList,
  landingPageLabels,
  isLoading,
  isLabelsLoading,
  deleteLoading,
}) => {
  const { data, paging = {} } = landingPageList || {}
  const { data: labelData, paging: labelPaging = {} } = landingPageLabels

  const navigate = useNavigate()
  const { t } = useTranslation()
  const [sort, setSort] = useState([{ field: 'name', sort: 'asc' }])
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [labelPage, setLabelPage] = useState(0)
  const [deleteLandingPageData, setDeleteLandingPageData] = useState()
  const [deleteLabelData, setDeleteLabelData] = useState()
  const [selectedLabel, setSelectedLabel] = useState([])

  const companyId = useSelector(state => state.auth.account?.companyId)

  const routeParams = useParams()
  const { type = '' } = routeParams || {}

  const isSystem = type === 'system'

  const isSystemRef = useRef(isSystem)

  const canView = usePermissions(isSystem ? 'landingpageSystem_view' : 'landingpagePerson_view')
  const canCreate = usePermissions(
    isSystem ? 'landingpageSystem_create' : 'landingpagePerson_create',
  )
  const canModify = usePermissions(
    isSystem ? 'landingpageSystem_modify' : 'landingpagePerson_modify',
  )
  const canDelete = usePermissions(
    isSystem ? 'landingpageSystem_delete' : 'landingpagePerson_delete',
  )

  useEffect(() => {
    if (isSystem !== isSystemRef) {
      setPage(0)
      setSort([{ field: 'name', sort: 'asc' }])
      setPageSize(10)
    }
  }, [isSystem])

  const form = useRef()

  const pagination = {
    ...paging,
    page,
    pageSize,
    onPageChange: setPage,
    onPageSizeChange: setPageSize,
  }

  const btnProps = {
    size: 'small',
    sx: { borderRadius: 2, padding: 0, color: theme => theme.palette.secondary_80.main },
  }
  const iconProps = {
    sx: {
      padding: 0.5,
    },
  }

  const cols = [
    {
      field: 'name',
      headerName: t('general.template_name'),
      width: 200,
    },
    // {
    //   field: 'htmlTitle',
    //   headerName: t('general.mail_subject'),
    //   width: 200,
    //   flex: 1,
    // },
    {
      field: 'labelNames',
      headerName: t('general.label'),
      width: 80,
    },
    {
      field: 'contain',
      headerName: t('general.contain'),
      width: 80,
    },
    {
      field: 'lastUpdateTime',
      headerName: t('general.update_date'),
      width: 200,
      valueFormatter: ({ value }) => (value ? moment(value).format(DATE_TIME_FORMAT) : '-'),
    },
    {
      field: 'edit',
      hidden: !canModify,
      headerName: t('general.edit'),
      width: 100,
      renderCell: ({ row }) => (
        <IconButton
          {...btnProps}
          onClick={() => {
            navigate(`/phish_template_landing/edit`, {
              state: { ...row, type: isSystem ? 'system' : 'individual' },
            })
          }}
        >
          <Edit {...iconProps} />
        </IconButton>
      ),
    },
    {
      field: 'preview',
      headerName: t('general.preview'),
      width: 100,
      renderCell: ({ row }) => (
        <IconButton
          {...btnProps}
          onClick={() => {
            dispatch({
              type: 'landingPage/getLandingPageHtml',
              payload: { landingpageTempId: row.landingpageTempId },
              successCallback: html => {
                const w = window.open('', '_blank', '') // access the "about:blank" window you've opened
                w.document.body.innerHTML = html
              },
            })
          }}
        >
          <Preview {...iconProps} />
        </IconButton>
      ),
    },
    // {
    //   field: 'delete',
    //   hidden: !canDelete,
    //   headerName: t('general.delete'),
    //   width: 100,
    //   renderCell: ({ row }) => (
    //     <IconButton
    //       {...btnProps}
    //       disabled={isSystem && companyId !== 1}
    //       onClick={() => {
    //         setDeleteLandingPageData(row)
    //       }}
    //     >
    //       <Close {...iconProps} />
    //     </IconButton>
    //   ),
    // },
    isSystem && companyId !== 1
      ? null
      : {
          field: 'delete',
          hidden: !canDelete,
          headerName: t('general.delete'),
          width: 100,
          renderCell: ({ row }) => (
            <IconButton
              {...btnProps}
              disabled={isSystem && companyId !== 1}
              onClick={() => {
                setDeleteLandingPageData(row)
              }}
            >
              <Close {...iconProps} />
            </IconButton>
          ),
        },
  ].filter(Boolean)

  const fetchData = useCallback(() => {
    const formValues = form.current?.getValues()
    const { search } = formValues || {}

    const payload = {
      searchParam: search,
      labelId: selectedLabel,
      sort: fieldToSortStr(get(sort, '[0]'), fieldMap),
      pageIndex: page,
      pageSize,
      type: isSystem ? 'S' : 'I',
    }

    dispatch({
      type: 'landingPage/getLandingPages',
      payload,
    })
  }, [dispatch, sort, page, pageSize, selectedLabel, isSystem, form])

  const deleteLandingPageTemplate = () => {
    if (deleteLandingPageData) {
      dispatch({
        type: 'landingPage/deleteLandingPage',
        payload: { landingpageTempId: deleteLandingPageData.landingpageTempId },
        successCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: {
              message: t('messages.email_template_deleted', {
                email_template: deleteLandingPageData.name,
              }),
              severity: 'success',
            },
          })
          setDeleteLandingPageData()
          fetchData()
        },
      })
    }
  }

  useEffect(() => {
    fetchData()
  }, [sort, page, pageSize, fetchData])

  const getLabels = () => {
    dispatch({
      type: 'landingPage/getLandingPageLabels',
      payload: { type: isSystem ? 'S' : 'I', pageIndex: labelPage, pageSize: 10 },
    })
  }

  const deleteLandingPageLabel = () => {
    if (deleteLabelData) {
      const { landingpageLabelId, labelName } = deleteLabelData

      dispatch({
        type: 'landingPage/deleteLandingPageLabel',
        payload: { labelId: landingpageLabelId },
        successCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: {
              message: t('messages.email_template_deleted', {
                email_template: labelName,
              }),
              severity: 'success',
            },
          })
          setDeleteLabelData()
          getLabels()
        },
      })
    }
  }

  useEffect(() => {
    getLabels()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSystem, labelPage, dispatch])

  if (!canView) return <Redirect />

  return (
    <Container maxWidth={false} sx={{ flex: 1, display: 'flex' }}>
      <Grid container spacing={2} flexDirection="column">
        <Grid item>
          <Grid container>
            <Grid item>
              <FormCreator
                ref={form}
                formItems={[
                  ...(canCreate && (companyId === 1 || !isSystem)
                    ? [
                        {
                          component: () => (
                            <Button
                              variant="text"
                              color="secondary_80"
                              endIcon={<AddCircle />}
                              onClick={() => {
                                navigate(
                                  `/phish_template_landing/${
                                    isSystem ? 'system' : 'individual'
                                  }/create`,
                                )
                              }}
                            >
                              {t('general.add_template')}
                            </Button>
                          ),
                        },
                      ]
                    : []),
                  {
                    label: t('general.quick_search'),
                    name: 'search',
                    defaultValue: '',
                    inputConfig: {
                      onSubmit: async () => {
                        setPage(0)
                        fetchData()
                      },
                    },
                  },
                ]}
                flexDirection="row"
                submitComponent={() => null}
                resetComponent={() => null}
              />
            </Grid>
          </Grid>

          <Grid container alignItems="center" justifyContent="space-between" py={2}>
            <Grid item>
              <LabelSelectList
                idKey="landingpageLabelId"
                nameKey="labelName"
                options={labelData ?? []}
                paging={labelPaging}
                page={labelPage}
                onPageChange={val => {
                  if (!isLabelsLoading) {
                    setLabelPage(val)
                  }
                }}
                onDelete={setDeleteLabelData}
                selected={selectedLabel}
                onSelect={label => {
                  if (selectedLabel === label) {
                    setSelectedLabel()
                  } else {
                    setSelectedLabel(label)
                  }
                }}
              />
            </Grid>

            <Grid item>
              <Stack direction="row">
                <Box flex={1} />
                <PermissionsController permission={'landingpageSystem_view'}>
                  <TableTabButton
                    selected={isSystem}
                    onClick={() => {
                      navigate('/phish_template_landing/system', { replace: true })
                      setSelectedLabel([])
                    }}
                    label={t('menu.system')}
                  />
                </PermissionsController>

                <PermissionsController permission={'landingpagePerson_view'}>
                  <TableTabButton
                    selected={!isSystem}
                    onClick={() => {
                      navigate('/phish_template_landing/individual', { replace: true })
                      setSelectedLabel([])
                    }}
                    label={t('menu.individual')}
                  />
                </PermissionsController>
              </Stack>
            </Grid>
          </Grid>
          <Table
            minHeight={400}
            columns={cols}
            rows={data || []}
            dataGridConfig={{
              sortingMode: 'server',
              sortingOrder: ['desc', 'asc'],
              sortModel: sort,
              onSortModelChange: newSort => setSort(newSort),
              getRowId: row => row.landingpageTempId,
              loading: isLoading,
            }}
            noRowsLabel={
              selectedLabel
                ? t('errors.no_templates_under_label')
                : t('errors.no_landing_page_templates')
            }
            pagination={pagination}
          />
        </Grid>
      </Grid>

      <ConfirmDialog
        open={!!deleteLandingPageData}
        title={t('messages.confirm_delete')}
        onClose={() => {
          setDeleteLandingPageData()
        }}
        onSubmit={() => {
          deleteLandingPageTemplate()
        }}
        loading={deleteLoading}
      >
        <Stack alignItems="center">
          <Typography>{`${deleteLandingPageData?.name}${t('messages.info')}?`}</Typography>
        </Stack>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!deleteLabelData}
        title={t('messages.confirm_delete')}
        onClose={() => {
          setDeleteLabelData()
        }}
        onSubmit={() => {
          deleteLandingPageLabel()
        }}
        loading={deleteLoading}
      >
        <Stack alignItems="center">
          <Typography>{`${deleteLabelData?.labelName}${t('messages.info')}?`}</Typography>
        </Stack>
      </ConfirmDialog>
    </Container>
  )
}

export default connect(mapStateToProps)(LandingPageTemplate)
